"use client";

import { useState, useEffect, useId, useRef, useMemo } from "react";
import { motion } from "motion/react"
import clsx from "clsx";

type FlipSegment = { text: string; styled: boolean };

function parseFlipWord(word: string): FlipSegment[] {
  return word.split(/(`[^`]*`)/g).filter(Boolean).map((part) =>
    part.startsWith("`") && part.endsWith("`")
      ? { text: part.slice(1, -1), styled: true }
      : { text: part, styled: false }
  );
}

export interface ContainerTextFlipProps {
  words?: string[];
  initialWord?: string;
  interval?: number;
  className?: string;
  textClassName?: string;
  animationDuration?: number;
  startDelay?: number;
}

export function TextFlip({
  words = [],
  initialWord,
  interval = 3000,
  className,
  textClassName,
  animationDuration = 800,
  startDelay = 2000,
}: ContainerTextFlipProps) {
  const id = useId();
  const initialWordRef = useRef<HTMLSpanElement>(null);
  const hasChangedRef = useRef(false);

  const animationWords = useMemo(() => {
    if (words.length === 0 && initialWord) {
      return [initialWord];
    }
    if (initialWord && !words.includes(initialWord)) {
      return [initialWord, ...words];
    }
    return words;
  }, [words, initialWord]);

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const textStyles = clsx("text-3xl xl:text-[36px] leading-normal whitespace-nowrap", textClassName);

  // Interval starts on mount so TextFlip stays in sync with WideMedia slider
  useEffect(() => {
    if (animationWords.length <= 1) return;
    const intervalId = setInterval(() => {
      hasChangedRef.current = true;
      setCurrentWordIndex(prev => (prev + 1) % animationWords.length);
    }, interval);
    return () => clearInterval(intervalId);
  }, [animationWords.length, interval]);

  // Enable blur animations after startDelay
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(true), startDelay);
    return () => clearTimeout(timer);
  }, [startDelay]);

  if (animationWords.length === 0) return null;

  const currentWord = animationWords[currentWordIndex];
  const plainCurrentWord = currentWord?.replace(/`([^`]*)`/g, "$1") ?? "";
  const shouldAnimate = isAnimating && hasChangedRef.current;

  return (
    <motion.span
      layout
      className={clsx("relative inline-block", className)}
    >
      <span
        className={clsx("invisible whitespace-nowrap", textStyles)}
        ref={initialWordRef}
        aria-hidden="true"
      >
        {plainCurrentWord}
      </span>
      <motion.span
        className={clsx("absolute left-0 top-0 whitespace-nowrap", textStyles)}
        key={currentWord}
        transition={{
          duration: animationDuration / 1000,
          ease: "easeInOut",
        }}
        layoutId={`word-div-${currentWord}-${id}`}
      >
        {(() => {
          const segments = parseFlipWord(currentWord);
          let globalIndex = 0;
          return segments.flatMap((segment: FlipSegment, segIdx: number) =>
            segment.text.split("").map((letter: string, letterIdx: number) => {
              const delay = globalIndex++ * 0.05;
              return (
                <motion.span
                  key={`${segIdx}-${letterIdx}`}
                  className={segment.styled ? "bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat" : undefined}
                  initial={shouldAnimate ? { opacity: 0, filter: "blur(10px)" } : { opacity: 1, filter: "blur(0px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  transition={{ delay }}
                >
                  {letter}
                </motion.span>
              );
            })
          );
        })()}
      </motion.span>
    </motion.span>
  );
}
